import React, { useState, useEffect, useContext } from 'react';
import { Redirect } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useFetch from '../../../../hooks/use-fetch';

import { CurrentUserContext } from '../../../../contexts/current-user';
import Spinner from '../../../../components/loader';
import Layout from '../../../../components/layout';
import FirmwareReleasesTable from './table';
import DeliveryLogTable from './delivery-log-table';
import FirmwareReleaseForm from './form';

const FirmwarePage = () => {
  const { t } = useTranslation();
  const [currentUserState] = useContext(CurrentUserContext);
  const devicesEnabled = currentUserState?.applications?.devices === true;
  const [loading, setLoading] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [deliveryLogs, setDeliveryLogs] = useState([]);
  const [devicesModels, setDevicesModels] = useState([]);
  const [item, setItem] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [actionRequest, setActionRequest] = useState(null);
  const [logReleaseId, setLogReleaseId] = useState('');
  const [logSerial, setLogSerial] = useState('');
  const [logQuery, setLogQuery] = useState('');

  const logsUrl = `/administration/devices/firmware_delivery_logs${logQuery}`;
  const actionUrl = actionRequest?.url || '/administration/devices/firmware_releases';

  const [{ response, isLoading }, doFetchTableData] = useFetch('/administration/devices/firmware_releases', true);
  const [{ response: devicesModelsResponse }, doFetchDevicesModels] = useFetch('/administration/directories/device_models', true);
  const [{ response: deliveryLogsResponse, isLoading: deliveryLogsLoading }, doFetchDeliveryLogs] = useFetch(logsUrl, true);
  const [{ response: actionResponse, isLoading: actionLoading }, doFetchAction] = useFetch(actionUrl, true);

  const headerData = {
    title: 'firmware',
    buttonsList: [
      {
        text: 'add_firmware_release',
        action: () => openModal(),
      },
    ],
  };

  useEffect(() => {
    if (!devicesEnabled) {
      return;
    }

    setLoading(true);
    doFetchTableData();
    doFetchDevicesModels();
  }, [devicesEnabled]);

  useEffect(() => {
    if (!devicesEnabled) {
      return;
    }

    doFetchDeliveryLogs();
  }, [devicesEnabled, logQuery]);

  useEffect(() => {
    if (!response) {
      return;
    }

    setTableData(response.data || []);
    setLoading(false);
  }, [response]);

  useEffect(() => {
    if (!devicesModelsResponse) {
      return;
    }

    setDevicesModels((devicesModelsResponse.data || []).map((model) => ({
      label: model.attributes.name,
      value: model.id,
    })));
  }, [devicesModelsResponse]);

  useEffect(() => {
    if (!deliveryLogsResponse) {
      return;
    }

    setDeliveryLogs(deliveryLogsResponse.data || []);
  }, [deliveryLogsResponse]);

  useEffect(() => {
    if (!actionRequest) {
      return;
    }

    doFetchAction({ method: actionRequest.method });
  }, [actionRequest]);

  useEffect(() => {
    if (!actionRequest || !actionResponse || actionLoading) {
      return;
    }

    setActionRequest(null);
    reloadTable();
  }, [actionRequest, actionResponse, actionLoading]);

  if (!devicesEnabled) {
    return <Redirect to="/admin/devices" />;
  }

  function closeModal() {
    setTimeout(() => {
      setItem('');
    }, 300);
    setIsOpen(false);
  }

  function openModal() {
    setIsOpen(true);
  }

  const reloadTable = () => {
    setLoading(true);
    doFetchTableData();
    doFetchDeliveryLogs();
  };

  const onItemEdit = (id) => {
    setItem(tableData.find((release) => release.id === id));
    openModal();
  };

  const onItemDelete = (id) => {
    if (!window.confirm(t('confirm_delete_firmware_release'))) {
      return;
    }

    setActionRequest({
      method: 'DELETE',
      url: `/administration/devices/firmware_releases/${id}`,
    });
  };

  const onItemToggleActive = (id, active) => {
    setActionRequest({
      method: 'PATCH',
      url: `/administration/devices/firmware_releases/${id}/${active ? 'deactivate' : 'activate'}`,
    });
  };

  const onShowLogs = (releaseId) => {
    setLogReleaseId(releaseId);
    setLogSerial('');
    setLogQuery(`?firmware_release_id=${releaseId}`);
  };

  const applyLogFilter = () => {
    const params = new URLSearchParams();

    if (logReleaseId) {
      params.set('firmware_release_id', logReleaseId);
    }
    if (logSerial) {
      params.set('serial', logSerial);
    }

    const query = params.toString();
    setLogQuery(query ? `?${query}` : '');
  };

  return (
    <Layout
      isWide={true}
      headerData={headerData}
    >
      {loading ? <Spinner /> : (
        <div className="mt-8 pb-10 space-y-8">
          <FirmwareReleasesTable
            tableData={tableData}
            onItemEdit={onItemEdit}
            onItemDelete={onItemDelete}
            onItemToggleActive={onItemToggleActive}
            onShowLogs={onShowLogs}
            isLoading={isLoading}
          />

          <section>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-4">
              <h3 className="text-base font-medium text-gray-900">{t('firmware_delivery_log')}</h3>
              <div className="flex flex-col md:flex-row gap-2">
                <select
                  aria-label={t('firmware_filter_release')}
                  value={logReleaseId}
                  onChange={(event) => setLogReleaseId(event.target.value)}
                  className="rounded-sm border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">{t('all_releases')}</option>
                  {tableData.map((release) => (
                    <option key={release.id} value={release.id}>
                      {release.attributes.version}
                    </option>
                  ))}
                </select>
                <input
                  aria-label={t('firmware_filter_serial')}
                  value={logSerial}
                  onChange={(event) => setLogSerial(event.target.value)}
                  className="rounded-sm border border-gray-300 px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={applyLogFilter}
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-blue-900 bg-blue-100 border border-transparent rounded-sm hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                >
                  {t('filters')}
                </button>
              </div>
            </div>
            {deliveryLogsLoading ? <Spinner /> : <DeliveryLogTable tableData={deliveryLogs} />}
          </section>
        </div>
      )}
      <FirmwareReleaseForm
        closeModal={closeModal}
        isOpen={isOpen}
        item={item}
        reloadTable={reloadTable}
        devicesModels={devicesModels}
      />
    </Layout>
  );
};

export default FirmwarePage;
